// Bulk import view — Blueprint §2 (Import) + F-IMPORT-*. Ports the SEMANTICS of
// legacy startImport / handleImportFile / showImportPreview / executeImport
// (v20 lines ~728-775, 5539-5816) onto the 2.0 data layer (data/import.js) + the
// store + the delegated data-action router.
//
// On screen:
//   header → two upload cards (New patients / Legacy patients) → Download-template
//   card → an inline preview area (shown after a file is parsed): programme
//   summary, a preview table (first rows, esc'd), the row-error / duplicate
//   lists, and Confirm-import / Cancel / Upload-different actions.
//
// THE CONFIRM (R-015): the irreversible "Confirm import" opens a focus-trapped
// openModal confirm (never native confirm()), with consequence copy, before the
// chunked RPC runs. EVIDENCE (R-025): a NEW-patient import lands staff-entered
// consent rows — the preview says plainly that each will need a verifier + the
// scanned consent at review before it can be promoted. The LEGACY success CTA is
// the drain path: "Assign these patients →" → Patients + the Legacy-pool scope.
//
// No inline handlers (strict CSP): data-action clicks + one delegated `change`
// listener for the hidden file input (added in wireOnce, not inline onchange).

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal } from '../ui/modal.js';
import { netErr } from '../errors.js';
import { registerView, navigate } from './registry.js';
import { setScopeMode } from '../data/scope.js';
import {
  parseWorkbook,
  writeTemplate,
  executeBulkImport,
  importProgressText,
  validateImportRows,
  summarizeProgrammes,
  programmeCodeSet,
  templateProgrammeCodes,
  countyFixRows,
  getXLSX,
} from '../data/import.js';
import { countyOptionsHTML } from '../data/counties.js';
import { scrollBehavior } from '../ui/motion.js';

// ─── SVG icons (no functional emoji; inline, CSP-safe — mirrors consents.js) ─

/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 16) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_FILE = '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>';
const IC_CLOCK = '<circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 2"/>';
const IC_DL = '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>';
const IC_WARN = '<path d="M12 4L2.5 20h19z"/><path d="M12 10v4m0 3v.5"/>';
const IC_OK = '<path d="M20 6 9 17l-5-5"/>';

// ─── Module state (transient preview; not a store slice) ────────────────────

/** @type {{mode: 'new'|'legacy', rows: any[], validation: any, progUnverified?: boolean}|null} */
let _preview = null;
let _pendingMode = /** @type {'new'|'legacy'} */ ('new');
let _wired = false;
let _busy = false; // block a double Confirm → no duplicate bulk import (legacy latch)

/** Test-only: reset module state. @returns {void} */
export function _resetImport() {
  _preview = null;
  _pendingMode = 'new';
  _wired = false;
  _busy = false;
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}
function session() {
  return store.get(SLICES.SESSION) || {};
}

const MAX_PREVIEW_ROWS = 15;

// XLSX.read runs synchronously on the UI thread. A very large or decompression-bomb
// workbook would freeze the tab (SheetJS expands zip entries in memory), so reject
// anything past this ceiling before reading. 15 MB is far above any realistic
// consent/legacy import spreadsheet.
const MAX_IMPORT_BYTES = 15 * 1024 * 1024;

// ─── Preview column model (legacy showImportPreview cols) ───────────────────

const PREVIEW_COLS = Object.freeze({
  new: [
    { k: 'patient_name', l: 'Name' },
    { k: 'programme', l: 'Programme' },
    { k: 'national_id', l: 'ID' },
    { k: 'patient_phone', l: 'Phone' },
    { k: 'county', l: 'County' },
  ],
  legacy: [
    { k: 'patient_name', l: 'Name' },
    { k: 'programme', l: 'Programme' },
    { k: 'national_id', l: 'ID' },
    { k: 'patient_phone', l: 'Phone' },
    { k: 'county', l: 'County' },
    { k: 'approximate_stage', l: 'Stage' },
    { k: 'status_hint', l: 'Status' },
  ],
});

// How many county fixers to render at once. A 4,000-row migration with a badly
// mapped county column would otherwise put 4,000 <select>s in the DOM; past this
// the panel says plainly that the file is the place to fix it.
const MAX_COUNTY_FIX = 25;

/** Cell value → display string (Dates → ISO date; empty → em dash). */
function cell(v) {
  if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  const s = v === null || v === undefined ? '' : String(v);
  return s.trim() ? s : '—';
}

// ─── Renderers ───────────────────────────────────────────────────────────────

/**
 * The R-032 county-fix panel: one canonical dropdown per row whose county is
 * missing or unrecognised, so a spelling slip is corrected here instead of
 * sending the user back to Excel for the whole file.
 *
 * Honesty rules this panel keeps:
 *  - it lists EVERY blocked row, not just the ones the truncated preview table
 *    shows (and states the cap when there are more than MAX_COUNTY_FIX);
 *  - the typed value is displayed verbatim and is never auto-applied — the
 *    matchCounty() guess appears in the option LABEL, and nothing changes until
 *    the user picks;
 *  - a fix edits the in-memory preview row only. Nothing is written until the
 *    Confirm-import handshake, and the corrected value goes up in the same
 *    payload shape v_20 sends.
 * @param {Array<Record<string, any>>} rows
 * @returns {string}
 */
function countyFixPanel(rows) {
  const bad = countyFixRows(rows);
  if (!bad.length) return '';
  const shown = bad.slice(0, MAX_COUNTY_FIX);
  const items = shown
    .map((b) => {
      const typedLabel = b.typed ? esc(b.typed) : '<i>blank</i>';
      return (
        '<li class="import-cfix-row">' +
        `<span class="import-cfix-n">Row ${esc(String(b.index + 1))}</span>` +
        `<span class="import-cfix-typed">typed: ${typedLabel}</span>` +
        `<select class="import-cfix-sel" data-action="import-county-fix" data-row="${esc(String(b.index))}" ` +
        `aria-label="County for row ${esc(String(b.index + 1))}">${countyOptionsHTML(b.typed, { placeholder: 'Select a county…' })}</select>` +
        '</li>'
      );
    })
    .join('');
  const more =
    bad.length > shown.length
      ? `<p class="import-cfix-more">Showing the first ${esc(String(shown.length))} of ${esc(String(bad.length))} rows with a county problem. ` +
        'Fix the rest in the spreadsheet and re-upload — correcting hundreds of rows one dropdown at a time is slower than fixing the column.</p>'
      : '';
  return (
    '<div class="import-banner warn import-cfix">' +
    `${ic(IC_WARN, 15)} <b>${esc(String(bad.length))} row${bad.length > 1 ? 's need' : ' needs'} a county</b> — pick the correct one below, or fix the column in the file and re-upload. ` +
    'Choosing here changes only this preview; nothing is saved until you confirm the import.' +
    `<ul class="import-cfix-list">${items}</ul>` +
    more +
    '</div>'
  );
}

function renderPreview() {
  const d = doc();
  const host = d && d.getElementById('import-results');
  if (!host) return;
  if (!_preview) {
    host.className = 'hidden';
    host.innerHTML = '';
    return;
  }
  host.className = '';

  const { mode, rows, validation, progUnverified } = _preview;
  const cols = PREVIEW_COLS[mode];
  const progs = summarizeProgrammes(rows);
  const hasErrors = validation.errorCount > 0;
  const dupCount = validation.dupIndices.size;

  const chips = progs
    .map((p) => `<span class="import-chip">${esc(p.code)}: ${esc(String(p.count))}</span>`)
    .join('');

  // R-025 evidence notice for NEW imports (staff-entered consents).
  const evidenceNote =
    mode === 'new'
      ? `<div class="import-note info">${ic(IC_WARN, 15)} These rows enter <b>New consents</b> as staff-entered consents. Each one needs a named verifier and the scanned consent attached at review before it can be promoted (R-025).</div>`
      : `<div class="import-note info">${ic(IC_CLOCK, 15)} These rows land directly in the patient board's <b>Triage</b> column, ownerless. Assign them from the Legacy pool afterwards.</div>`;

  // Error / all-clear banner.
  const errBanner = hasErrors
    ? `<div class="import-banner danger"><b>${esc(String(validation.errorCount))} row${validation.errorCount > 1 ? 's have' : ' has'} issues</b> — fix them in the file and re-upload. Import is blocked until every row is valid.<ul>${validation.messages
        .slice(0, 20)
        .map((m) => `<li>${esc(m)}</li>`)
        .join('')}${validation.messages.length > 20 ? `<li>…and ${esc(String(validation.messages.length - 20))} more</li>` : ''}</ul></div>`
    : `<div class="import-banner ok">${ic(IC_OK, 15)} <b>All ${esc(String(rows.length))} rows look valid.</b> Ready to import.</div>`;

  const dupBanner =
    dupCount > 0
      ? `<div class="import-banner warn">${ic(IC_WARN, 15)} <b>${esc(String(dupCount))} in-file duplicate${dupCount > 1 ? 's' : ''}</b> (same phone, ID, or name as an earlier row). The importer de-duplicates, so these will be skipped on the server.</div>`
      : '';

  // Honest caveat when the programme catalogue never loaded: the code check was
  // skipped, so "valid" here does NOT mean the programme codes were verified. The
  // server RPC remains the real gate and will reject unknown codes.
  const progBanner = progUnverified
    ? `<div class="import-banner warn">${ic(IC_WARN, 15)} <b>Programme catalogue unavailable</b> — programme codes were not verified against the catalogue. The server will still reject unknown codes on import.</div>`
    : '';

  // Preview table (first N rows), each row flagged if it has errors / is a dup.
  const shown = rows.slice(0, MAX_PREVIEW_ROWS);
  const errorRowSet = new Set(validation.rowErrors.map((/** @type {any} */ e) => e.row - 1));
  const head = `<tr><th scope="col">#</th>${cols.map((c) => `<th scope="col">${esc(c.l)}</th>`).join('')}<th scope="col">Check</th></tr>`;
  const bodyRows = shown
    .map((r, i) => {
      const bad = errorRowSet.has(i);
      const dup = validation.dupIndices.has(i);
      const flag = bad
        ? `<span class="today-tag danger">Fix</span>`
        : dup
          ? `<span class="today-tag warn">Duplicate</span>`
          : `<span class="today-tag ok">OK</span>`;
      return (
        `<tr class="${bad ? 'import-row-bad' : ''}"><td>${esc(String(i + 1))}</td>` +
        cols.map((c) => `<td>${esc(cell(r[c.k]))}</td>`).join('') +
        `<td>${flag}</td></tr>`
      );
    })
    .join('');
  const moreRow =
    rows.length > MAX_PREVIEW_ROWS
      ? `<tr><td colspan="${cols.length + 2}" class="import-more">…and ${esc(String(rows.length - MAX_PREVIEW_ROWS))} more rows</td></tr>`
      : '';

  const confirmBtn = hasErrors
    ? `<button type="button" class="btn pri" disabled aria-disabled="true">Import ${esc(String(rows.length))} patients</button>`
    : `<button type="button" class="btn pri" data-action="import-confirm">Import ${esc(String(rows.length))} patients</button>`;

  host.innerHTML =
    '<div class="card">' +
    `<div class="chd"><h3>${esc(mode === 'legacy' ? 'Legacy' : 'New')} patient preview — ${esc(String(rows.length))} rows</h3></div>` +
    '<div class="cbd">' +
    `<div class="import-summary">${chips}<span class="import-total">${esc(String(rows.length))} total</span></div>` +
    evidenceNote +
    errBanner +
    countyFixPanel(rows) +
    progBanner +
    dupBanner +
    `<div class="import-table-wrap" tabindex="0" aria-label="Import preview rows"><table class="import-table"><thead>${head}</thead><tbody>${bodyRows}${moreRow}</tbody></table></div>` +
    '<div class="import-actions">' +
    confirmBtn +
    '<button type="button" class="btn ghost" data-action="import-cancel">Cancel</button>' +
    `<button type="button" class="btn ghost" data-action="import-pick" data-mode="${esc(mode)}">Upload a different file</button>` +
    '</div>' +
    '</div></div>';
}

/** Replace the preview area with the post-import result + drain CTA. */
function renderResult(mode, result) {
  const d = doc();
  const host = d && d.getElementById('import-results');
  if (!host) return;
  host.className = '';
  const { imported, errors, duplicates } = result;

  const errList =
    errors.length > 0
      ? `<div class="import-banner danger"><b>${esc(String(errors.length))} error${errors.length > 1 ? 's' : ''} from the server:</b><ul>${errors
          .slice(0, 30)
          .map((e) => `<li>${esc(String(e))}</li>`)
          .join('')}${errors.length > 30 ? `<li>…and ${esc(String(errors.length - 30))} more</li>` : ''}</ul></div>`
      : '';
  const dupList =
    duplicates.length > 0
      ? `<div class="import-banner warn"><b>${esc(String(duplicates.length))} duplicate${duplicates.length > 1 ? 's' : ''} skipped</b> (already in the system):<ul>${duplicates
          .slice(0, 50)
          .map((x) => `<li>${esc(String(x))}</li>`)
          .join('')}${duplicates.length > 50 ? `<li>…and ${esc(String(duplicates.length - 50))} more</li>` : ''}</ul></div>`
      : '';

  const summaryLine =
    imported > 0
      ? `<div class="import-banner ok">${ic(IC_OK, 15)} <b>${esc(String(imported))} patient${imported > 1 ? 's' : ''} imported.</b> ${mode === 'legacy' ? 'They are in the Triage column of the patient board, waiting to be assigned.' : 'They are in New consents, waiting for review and acceptance.'}</div>`
      : `<div class="import-banner danger"><b>Nothing was imported.</b> See the errors below and re-upload.</div>`;

  const cta =
    mode === 'legacy'
      ? `<button type="button" class="btn pri" data-action="import-goto" data-target="patients-legacy">Assign these patients →</button>`
      : `<button type="button" class="btn pri" data-action="import-goto" data-target="consents">Go to New consents →</button>`;

  host.innerHTML =
    '<div class="card"><div class="chd"><h3>Import complete</h3></div><div class="cbd">' +
    summaryLine +
    dupList +
    errList +
    '<div class="import-actions">' +
    (imported > 0 ? cta : '') +
    '<button type="button" class="btn ghost" data-action="import-cancel">Import more</button>' +
    '</div></div></div>';
}

// ─── uiConfirm (openModal-based; native confirm() BANNED, R-015) ────────────

/**
 * A focus-trapped yes/no confirm over the shared modal (three-part copy).
 * @param {{title: string, message: string, confirmLabel: string, danger?: boolean}} o
 * @returns {Promise<boolean>}
 */
function uiConfirm(o) {
  return new Promise((resolve) => {
    openModal(o.title, '', `<p class="import-confirm">${esc(o.message)}</p>`, [
      { l: 'Cancel', k: 'cancel', fn: () => { closeModal(); resolve(false); } },
      { l: o.confirmLabel, v: o.danger ? 'danger' : 'pri', k: 'confirm', fn: () => { closeModal(); resolve(true); } },
    ]);
  });
}

// ─── Flow ────────────────────────────────────────────────────────────────────

/** Trigger the hidden file input for a mode (legacy startImport). */
function pickFile(mode) {
  _pendingMode = mode === 'legacy' ? 'legacy' : 'new';
  if (!getXLSX()) {
    toast('The spreadsheet reader failed to load — reload when online and try again', 'err');
    return;
  }
  const d = doc();
  const input = d && d.getElementById('import-file-input');
  if (input) input.click();
}

/**
 * Build + render a preview from already-parsed mapped rows (also the seam the
 * dev harness drives headless). Pure-ish: validates against the loaded
 * programme catalogue.
 * @param {'new'|'legacy'} mode
 * @param {any[]} rows mapped rows
 * @returns {void}
 */
export function showPreview(mode, rows) {
  const m = mode === 'legacy' ? 'legacy' : 'new';
  const progSet = programmeCodeSet();
  const validation = validateImportRows(rows, m, { programmes: progSet });
  // When the catalogue failed to load at boot the set is empty and validateRow
  // skips the programme-code check entirely — the preview would then advertise
  // rows as valid that the catalogue could reject. Flag it so the banner is honest.
  _preview = { mode: m, rows, validation, progUnverified: !progSet.size };
  renderPreview();
  const d = doc();
  const host = d && d.getElementById('import-results');
  if (host && host.scrollIntoView) host.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
}

/** Read a chosen File, parse it for the pending mode, render the preview. */
function onFileChosen(file) {
  if (!file) return;
  if (file.size > MAX_IMPORT_BYTES) {
    toast('File too large — split it into smaller sheets and re-upload', 'err');
    return;
  }
  toast('Reading file…');
  const reader = new /** @type {any} */ (globalThis).FileReader();
  reader.onload = (e) => {
    const buf = e && e.target && e.target.result;
    const res = parseWorkbook(buf, _pendingMode);
    if (!res.ok) {
      toast(res.reason, 'err');
      return;
    }
    showPreview(_pendingMode, res.rows);
  };
  reader.onerror = () => toast('Could not read the file', 'err');
  reader.readAsArrayBuffer(file);
}

/** Download the .xlsx template (legacy downloadTemplate). */
function onTemplate() {
  const res = writeTemplate(templateProgrammeCodes());
  if (!res.ok) {
    toast(res.code === 'NO_XLSX' ? 'Template unavailable — a script failed to load; reload when online' : netErr(res.error, 'Template download failed'), 'err');
    return;
  }
  toast('Template downloaded', 'ok');
}

/** Confirm (R-015) then run the chunked import (legacy executeImport). */
async function onConfirm() {
  if (_busy || !_preview) return;
  const { mode, rows, validation } = _preview;
  if (validation.errorCount > 0) {
    toast('Fix the flagged rows before importing', 'err');
    return;
  }
  const go = await uiConfirm({
    title: `Import ${rows.length} ${mode === 'legacy' ? 'legacy' : 'new'} patient${rows.length > 1 ? 's' : ''}?`,
    message:
      mode === 'legacy'
        ? `This creates ${rows.length} patient record${rows.length > 1 ? 's' : ''} directly in the Triage queue. It cannot be undone from here — duplicates are skipped automatically.`
        : `This adds ${rows.length} consent submission${rows.length > 1 ? 's' : ''} to New consents for review. It cannot be undone from here — duplicates are skipped automatically.`,
    confirmLabel: `Import ${rows.length}`,
  });
  if (!go) return;

  _busy = true;
  toast('Importing…');
  const res = await executeBulkImport(rows, mode, {
    // One toast per finished batch (rejected batches included), none after the
    // last — the result toast follows. The rule lives in importProgressText (R-16).
    onProgress: (done, total) => {
      const msg = importProgressText(done, total);
      if (msg) toast(msg);
    },
  });
  _busy = false;

  const data = res.data || { imported: 0, total: 0, errors: [], duplicates: [] };
  if (!res.ok) {
    toast(netErr(res.error, 'Import failed'), 'err');
    // Still show whatever the server managed (partial) so nothing is hidden.
    if (data.imported > 0) {
      _preview = null;
      renderResult(mode, data);
    }
    return;
  }

  const parts = [`${data.imported} imported`];
  if (data.duplicates.length) parts.push(`${data.duplicates.length} duplicate${data.duplicates.length > 1 ? 's' : ''} skipped`);
  if (data.errors.length) parts.push(`${data.errors.length} error${data.errors.length > 1 ? 's' : ''}`);
  toast(parts.join(', '), data.errors.length && data.imported === 0 ? 'err' : 'ok');

  _preview = null;
  renderResult(mode, data);
}

/**
 * Apply one county dropdown choice to the in-memory preview and re-validate the
 * WHOLE set (R-032). Re-validating everything rather than patching the row's own
 * error list is deliberate: the row-error list, the "N rows have issues" banner
 * and the Confirm gate all read one validation result, so they cannot disagree
 * about whether the import is still blocked (the P5 lesson, applied to a
 * partial update rather than a partial load).
 * @param {any} el the <select> that changed
 * @returns {void}
 */
function onCountyFix(el) {
  if (!_preview) return;
  const idx = parseInt(el.getAttribute('data-row') || '', 10);
  const row = _preview.rows[idx];
  if (!row) return;
  const value = el.value;
  if (!value) return; // placeholder — the user has not chosen yet
  row.county = value;
  const progSet = programmeCodeSet();
  _preview.validation = validateImportRows(_preview.rows, _preview.mode, { programmes: progSet });
  _preview.progUnverified = !progSet.size;
  renderPreview();
  // The re-render destroyed the <select> the user was standing on. Land focus on
  // the next remaining fixer so a keyboard user can work down the list without
  // re-finding it; when the last one is fixed, focus the Confirm button instead.
  const d = doc();
  const next =
    (d && d.querySelector('[data-action="import-county-fix"]')) ||
    (d && d.querySelector('[data-action="import-confirm"]'));
  if (next && next.focus) next.focus();
}

/** Cancel/reset the preview back to the empty upload state. */
function onCancel() {
  _preview = null;
  renderPreview();
}

/** Post-import CTA navigation (legacy drain path). */
function onGoto(el) {
  const target = el.getAttribute('data-target');
  if (target === 'patients-legacy') {
    const s = session();
    setScopeMode(s.user && s.user.id, 'legacy', s.role);
    navigate('patients');
  } else if (target === 'consents') {
    navigate('consents');
  }
}

// ─── Wiring ──────────────────────────────────────────────────────────────────

function wireOnce() {
  if (_wired) return;
  _wired = true;
  register('import-pick', (el) => pickFile(el.getAttribute('data-mode') || 'new'));
  register('import-template', () => onTemplate());
  register('import-confirm', () => void onConfirm());
  register('import-cancel', () => onCancel());
  register('import-goto', (el) => onGoto(el));

  // Hidden file input: delegated `change` listener (NOT inline onchange). One
  // document-level listener, CSP-safe, survives re-renders of the input.
  const d = doc();
  if (d && d.addEventListener) {
    d.addEventListener('change', (ev) => {
      const t = /** @type {any} */ (ev.target);
      if (!t || t.id !== 'import-file-input') return;
      const file = t.files && t.files[0];
      t.value = ''; // allow re-uploading the same file
      onFileChosen(file);
    });
    // County fixers (R-032). A <select> fires `change`, not `click`, so it
    // cannot ride the data-action click router — same delegated pattern as the
    // file input, one listener, CSP-safe, survives every preview re-render.
    d.addEventListener('change', (ev) => {
      const t = /** @type {any} */ (ev.target);
      if (!t || t.getAttribute?.('data-action') !== 'import-county-fix') return;
      onCountyFix(t);
    });
  }
}

function card(mode, iconPaths, title, blurb) {
  return (
    `<button type="button" class="import-card" data-action="import-pick" data-mode="${esc(mode)}">` +
    `<span class="import-card-ic">${ic(iconPaths, 34)}</span>` +
    `<span class="import-card-h">${esc(title)}</span>` +
    `<span class="import-card-b">${esc(blurb)}</span>` +
    '<span class="btn pri import-card-btn">Upload Excel</span>' +
    '</button>'
  );
}

function skeleton() {
  return (
    '<div id="import-view" class="import">' +
    '<header class="view-head"><h1>Bulk import</h1>' +
    '<p class="import-sub">Upload patients from physical consent forms, or migrate existing programme data.</p></header>' +
    '<div class="import-cards">' +
    card('new', IC_FILE, 'New patients', 'Physical consent forms. These enter the New consents queue for review before joining the patient board.') +
    card('legacy', IC_CLOCK, 'Legacy patients', 'Existing programme patients. They land in a Triage queue for nurses to review and place at the correct stage.') +
    '</div>' +
    '<div class="card import-template-card">' +
    '<div class="chd"><h3>Excel template</h3>' +
    '<button type="button" class="btn pri sm" data-action="import-template">' + ic(IC_DL, 14) + 'Download template</button></div>' +
    '<div class="cbd"><p class="import-template-note">Three sheets: <b>New Patients</b> (physical consents → New consents review), <b>Legacy Patients</b> (existing patients → Triage), and a <b>Reference</b> sheet listing valid programmes, counties, and stages. Every row is re-validated on upload. Download it, fill it in, then upload above.</p></div>' +
    '</div>' +
    '<div id="import-results" class="hidden"></div>' +
    // Hidden file input — data-action marks it; the change listener (wireOnce)
    // reads it. Not an inline onchange (strict CSP).
    '<input type="file" id="import-file-input" accept=".xlsx,.xls" class="visually-hidden" data-action="import-file" aria-hidden="true" tabindex="-1"/>' +
    '</div>'
  );
}

/**
 * Register the Bulk import view, replacing the placeholder. Call once at boot.
 * @returns {void}
 */
export function registerImport() {
  registerView('import', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      renderPreview(); // restore a preview if one survives a re-render
    },
  });
}
